import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { logger } from '../services/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch React component errors
 * Displays user-friendly error page and logs errors with component stack
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error with component stack to logger service
    logger.error('React component error caught by ErrorBoundary', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });

    // Update state with full error info
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleRefresh = (): void => {
    // Clear error state and reload page
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  private handleGoHome = (): void => {
    // Navigate to home page
    window.location.href = '/';
  };

  private toggleDetails = (): void => {
    const details = document.getElementById('error-details');
    if (details) {
      details.classList.toggle('hidden');
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      const { error, errorInfo } = this.state;
      const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'development';

      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-8">
            {/* Error Icon */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-full">
                <AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center mb-3">
              Something went wrong
            </h1>

            {/* Error Description */}
            <p className="text-slate-600 dark:text-slate-400 text-center mb-8">
              We encountered an unexpected error while rendering this page. Our team has been
              notified and we're working to fix the issue.
            </p>

            {/* Error Message (if available) */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-mono text-red-800 dark:text-red-200">
                  {error.toString()}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                onClick={this.handleRefresh}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-lg font-medium transition-colors"
              >
                <Home className="w-5 h-5" />
                Go to Dashboard
              </button>
            </div>

            {/* Technical Details (Development Only or Expandable) */}
            {(isDevelopment || errorInfo) && (
              <div>
                <button
                  onClick={this.toggleDetails}
                  className="w-full text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline mb-2"
                >
                  {isDevelopment ? 'Show' : 'Show'} technical details
                </button>
                <div
                  id="error-details"
                  className={`p-4 bg-slate-100 dark:bg-slate-800 rounded-lg overflow-auto max-h-96 ${
                    isDevelopment ? '' : 'hidden'
                  }`}
                >
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    Error Stack:
                  </h3>
                  <pre className="text-xs text-slate-700 dark:text-slate-300 font-mono whitespace-pre-wrap mb-4">
                    {error?.stack || 'No stack trace available'}
                  </pre>

                  {errorInfo && (
                    <>
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">
                        Component Stack:
                      </h3>
                      <pre className="text-xs text-slate-700 dark:text-slate-300 font-mono whitespace-pre-wrap">
                        {errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Support Message */}
            <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-800">
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                If this problem persists, please contact support with the error details above.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // No error - render children normally
    return this.props.children;
  }
}

/**
 * Functional wrapper for easier use in function components
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
): React.FC<P> => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
};
